import { Divider } from '@mui/material';
import React from 'react';
import {
  ToneType,
  VoiceType
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/ContentServerProxy/types';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import ScrollableContainer from 'src/components/BeaconV2Components/ScrollableContainer';

import {
  SlDialog,
  SlDialogCloseButton,
  SlDialogContent,
  SlDialogFooter,
  SlDialogHeader,
  SlDialogRoot,
  SlDialogTitle
} from 'src/components/BeaconV2Components/SlDialog';

export const voiceDefinitions = [
  {
    name: VoiceType.Informative,
    description:
      'An informative voice aims to educate, explain, or provide insights in a manner that is easily digestible and memorable for the audience, enhancing their comprehension and retention of the presented information.'
  },
  {
    name: VoiceType.Persuasive,
    description:
      "A persuasive voice often engages emotionally and intellectually, uses relatable examples, and appeals to logic, or credibility to build a compelling case to sway the audience's opinion in a specific direction."
  },
  {
    name: VoiceType.Sustainable,
    description:
      'A sustainable voice helps to build trust around sustainable products, encouraging the audience to make environmentally conscious, socially responsible, and economically viable decisions.'
  },
  {
    name: VoiceType.Healthy,
    description:
      "A healthy voice reinforces a message of wellness and vitality, creating a congruent auditory experience that aligns with the product's health benefits, and enhances the credibility of health claims."
  },
  {
    name: VoiceType.Playful,
    description:
      'A playful voice helps to establish a positive emotional connection with the audience and presents as lively, cheerful, and engaging, while conveying a sense of fun, amusement, and lightheartedness.'
  },
  {
    name: VoiceType.Supportive,
    description:
      "A supportive voice provides warm, empathetic, and reassuring feelings for the audience while reinforcing the product's nurturing and helpful qualities, and purpose of providing aid, comfort or assistance."
  }
];

export const toneDefinitions = [
  {
    name: ToneType.Friendly,
    description:
      'A friendly tone refers to a warm, approachable, relatable, casual, and inviting manner of communication that makes the product seem accessible, easy to use, and beneficial to the audience.'
  },
  {
    name: ToneType.Luxurious,
    description:
      "A luxurious tone conveys a sense of opulence, exclusivity, elegance, and high quality while emphasizing the product's prestigious origins, limited availability, or association with affluent lifestyles."
  },
  {
    name: ToneType.Humorous,
    description:
      'A humorous tone employs wit, jokes, and playful language to create an entertaining and lighthearted portrayal of the product to the audience to associate memorable, relatable, and appealing feelings.'
  },
  {
    name: ToneType.Encouraging,
    description:
      "An encouraging tone adopts a supportive and motivational approach that inspires confidence in the audience's ability to use and benefit from the product while emphasizing positive outcomes."
  },
  {
    name: ToneType.Enthusiastic,
    description:
      "An enthusiastic tone conveys a genuine excitement and passion about the product's features, benefits, and potential impact on the audience's life to spark interest and engagement."
  },
  {
    name: ToneType.Confident,
    description:
      "A confident tone projects a strong sense of assurance and certainty about the product's quality, effectiveness, superiority, reliability, value, and ability to meet or exceed the audience's expectations."
  }
];

export const Definition = ({ name, description }: { name: string; description: string }) => {
  return (
    <Flex flexDirection="column" gap="sm">
      <Text variant="body2" fontWeight="500">
        {name}
      </Text>
      <Text variant="body2">{description}</Text>
    </Flex>
  );
};

export const VoiceAndTonePagePopup = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  return (
    <SlDialogRoot>
      <SlDialog onClose={onClose} open={open} width={752} height={641}>
        <SlDialogHeader>
          <SlDialogTitle>Voice & Tone Definitions</SlDialogTitle>
          <SlDialogCloseButton onClick={onClose} />
        </SlDialogHeader>

        <ScrollableContainer scrollbarWidth="5px" thumbColor="#BEC7D3">
          <SlDialogContent sx={{ overflowX: 'hidden', paddingX: '48px' }}>
            <Text variant="h6" fontWeight="500" sx={{ marginBottom: '16px' }}>
              Voice Definitions
            </Text>
            <Flex flexDirection="column" sx={{ gap: '24px' }}>
              {voiceDefinitions.map((definition) => (
                <Definition key={definition.name} {...definition} />
              ))}
            </Flex>

            <Divider sx={{ margin: '32px 0' }} />

            <Text variant="h6" fontWeight="500" sx={{ marginBottom: '16px' }}>
              Tone Definitions
            </Text>
            <Flex flexDirection="column" sx={{ gap: '24px' }}>
              {toneDefinitions.map((definition) => (
                <Definition key={definition.name} {...definition} />
              ))}
            </Flex>
          </SlDialogContent>
        </ScrollableContainer>

        <SlDialogFooter
          sx={{
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <Flex justifyContent="center">
            <SlButton onClick={onClose}>Close</SlButton>
          </Flex>
        </SlDialogFooter>
      </SlDialog>
    </SlDialogRoot>
  );
};
